import { SearchIcon, SettingsIcon } from "@chakra-ui/icons";
import {
  Badge,
  Button,
  Checkbox,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Link,
  Tooltip,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, OnChangeFn, Row, RowSelectionState } from "@tanstack/react-table";
import { useRouter } from "@uirouter/react";
import React from "react";
import AgencyMemberSelect from "../../shared/components/AgencyMemberSelect";
import AgencyTeamSelect from "../../shared/components/AgencyTeamSelect";
import DebouncedInput from "../../shared/components/DebouncedInput";
import OfficesSelect from "../../shared/components/OfficeSelect";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import DataTable from "../../shared/components/DataTable/DataTable";
import useGraphQLDataTable from "../../shared/components/DataTable/useGraphQLDataTable";
import { useClockedVisitsNewAiCapabilities } from "./clockedVisitsNew.ai";
import { GET_CLOCKED_VISIT_INSTANCES_QUERY } from "./cico-reports.graphql";
import {
  EvvVisitStatus,
  GetClockedVisitInstancesQuery,
  GetClockedVisitInstancesQueryVariables,
} from "../../shared/schema/gql/graphql";
import { LocalDate } from "@js-joda/core";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { toArrayOrNull } from "../../shared/utils/common";
import ContractTypeSelect from "../../shared/components/ContractTypeSelect";
import { useEntityLink } from "../../shared/hooks/useEntityLink";
import { EvvStatusCell } from "./components/EvvStatusCell";
import { ClockLocationCell } from "./components/ClockLocationCell";
import { ResubmitConfirmationDialog } from "./components/ResubmitConfirmationDialog";
import { SandataResubmitUploadModal } from "./components/SandataResubmitUploadModal";
import { SandataPayloadCell } from "./components/SandataPayloadCell";
import { getEvvStatus } from "./utils/evv-status";
import { auth } from "../../core/auth";
import { FC_AGENCY_ID } from "../../shared/consts";
import useApi from "../../shared/hooks/useApi";
import { VisitInstanceId } from "../../shared/schema/schema";
import { useEvvDevHelpAvailability } from "../evv-dev-help/evv-dev-help.hooks";
import {
  getResubmitAggregatorFor,
  MAX_RESUBMIT_VISITS,
  ResubmitAggregator,
  useResubmitEvvVisits,
} from "./hooks/useResubmitEvvVisits";
import { EvvDevHelpBadge } from "../evv-dev-help/components/EvvDevHelpBadge";
import { EvvDevHelpModal } from "../evv-dev-help/components/EvvDevHelpModal";
import {
  EVV_DEV_HELP_MAX_VISITS_PER_REQUEST,
  EvvDevHelpVisitStatusEntry,
  getEvvDevHelpVisitStatuses,
} from "../evv-dev-help/evv-dev-help.api";

const today = LocalDate.now().toString();

const statusOptions = [
  { value: EvvVisitStatus.NotCompleted, label: "Not Completed" },
  { value: EvvVisitStatus.PendingSubmission, label: "Pending Submission" },
  { value: EvvVisitStatus.Submitted, label: "Submitted" },
  { value: EvvVisitStatus.Failed, label: "Failed" },
];

function formatFullName(
  person: { firstName: string; middleName?: string | null; lastName: string } | null | undefined
): string {
  if (!person) return "";
  return [person.firstName, person.middleName, person.lastName].filter(Boolean).join(" ");
}

// Schema augmentation: webapp's checked-in schema.graphql predates the server's
// `hhaexchangeSubmission` field, but the GraphQL query in cico-reports.graphql.ts
// requests it and the server returns it. Once `pnpm gql:generate` is re-run with
// the updated server schema, this augmentation can be removed.
type ClockedVisitInstance = GetClockedVisitInstancesQuery["clockedVisitInstances"]["nodes"][0] & {
  hhaexchangeSubmission: {
    submitted: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Instant; matches existing pattern in evv-status.ts
    sentAt: any | null;
    errorType: string | null;
    errorMessage: string | null;
    transactionId: string | null;
  } | null;
};

const { accessor, display } = createColumnHelper<ClockedVisitInstance>();

const EVV_DEV_HELP_STATUSES_KEY = "evv_dev_help_visit_statuses";

const devHelpStatusOptions = [
  { value: "AWAITING_DEV", label: "Awaiting dev" },
  { value: "HANDLED", label: "Handled" },
];

function getResubmittableAggregator(row: ClockedVisitInstance): ResubmitAggregator | null {
  return getResubmitAggregatorFor(row.contract?.contractType.evvAggregator);
}

export default function ClockedVisitsGraphQLRoute() {
  const entityLink = useEntityLink();
  const { queries } = useApi();
  const queryClient = useQueryClient();
  const confirmationDisclosure = useDisclosure();
  const uploadDisclosure = useDisclosure();
  const devHelpDisclosure = useDisclosure();

  // Row selection state for re-submit. `selectedVisits` mirrors `rowSelection` but holds the
  // original row data so cross-page selections survive pagination (TanStack's
  // `getSelectedRowModel()` only sees rows in the current page's `data`).
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [selectedVisits, setSelectedVisits] = React.useState<Map<string, ClockedVisitInstance>>(
    new Map()
  );

  // Holds the current page's nodes so the row-selection change handler can resolve
  // newly-selected ids back to their row data (the handler is created before the query
  // returns, so we read through a ref kept in sync below).
  const nodesRef = React.useRef<ClockedVisitInstance[]>([]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = React.useCallback((updater) => {
    setRowSelection((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const byId = new Map(nodesRef.current.map((n) => [String(n.id), n]));
      setSelectedVisits((map) => {
        const out = new Map(map);
        for (const id of Object.keys(prev)) {
          if (!next[id]) out.delete(id);
        }
        for (const id of Object.keys(next)) {
          if (next[id] && !out.has(id)) {
            const node = byId.get(id);
            if (node) out.set(id, node);
          }
        }
        return out;
      });
      return next;
    });
  }, []);

  const { stateService } = useRouter();
  const hasResubmitPermission = auth.isAllowedTo("resubmit_evv_records");
  const hasDutyCodeMappingPermission = auth.isAllowedTo("view_evv_duty_code_mapping");
  const isFCAgency = auth.getAuthOrFail().data.agency.id === FC_AGENCY_ID;

  const certificationsQuery = useQuery(queries.caregiver.certifications());
  const certificationOptions = React.useMemo(
    () =>
      (certificationsQuery.data ?? []).map((c) => ({
        value: c.certification,
        label: c.certification,
      })),
    [certificationsQuery.data]
  );

  // Office name lookup — the GraphQL query only returns `officeId`, so resolve names
  // from the office list (same source the OfficesSelect filter uses).
  const officesQuery = useQuery(queries.office.list());
  const officeNameById = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const office of officesQuery.data?.offices ?? []) {
      map.set(String(office.id), office.name);
    }
    return map;
  }, [officesQuery.data]);

  // Service code filter options. Includes inactive codes on purpose — the page filters
  // historical visits, which may reference a code that has since been deactivated.
  const serviceCodesQuery = useQuery(queries.serviceCode.list());
  const serviceCodeOptions = React.useMemo(
    () =>
      (serviceCodesQuery.data?.serviceCodes ?? [])
        .map((serviceCode) => ({ value: serviceCode.id, label: serviceCode.code }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [serviceCodesQuery.data]
  );

  // Dev-help badges: one batched call for the rows on screen. The ids are set from an
  // effect below because the table (and so the rows) is created after the columns.
  // Only FreedomCare has the feature. Fails closed: hidden while loading or on error, and
  // the statuses call (which would 403 elsewhere) is never made unless enabled.
  const devHelpAvailabilityQuery = useEvvDevHelpAvailability();
  const isDevHelpEnabled = devHelpAvailabilityQuery.data?.enabled === true;

  const [visibleVisitIds, setVisibleVisitIds] = React.useState<number[]>([]);
  const devHelpStatusesQuery = useQuery({
    queryKey: [EVV_DEV_HELP_STATUSES_KEY, visibleVisitIds],
    queryFn: () => getEvvDevHelpVisitStatuses(visibleVisitIds),
    enabled: isDevHelpEnabled && visibleVisitIds.length > 0,
  });
  const devHelpStatusByVisitId = React.useMemo(() => {
    const map = new Map<number, EvvDevHelpVisitStatusEntry>();
    for (const entry of devHelpStatusesQuery.data ?? []) {
      map.set(entry.visitInstanceId, entry);
    }
    return map;
  }, [devHelpStatusesQuery.data]);

  // Define columns inside component to access hooks
  const columns = [
    // Checkbox column only shown if user has resubmit permission
    ...(hasResubmitPermission
      ? [
          display({
            id: "_select",
            header: ({ table }) => (
              <Checkbox
                isChecked={table.getIsAllPageRowsSelected()}
                isIndeterminate={table.getIsSomePageRowsSelected()}
                onChange={table.getToggleAllPageRowsSelectedHandler()}
              />
            ),
            cell: ({ row }) => (
              <Checkbox
                isChecked={row.getIsSelected()}
                isDisabled={!row.getCanSelect()}
                onChange={row.getToggleSelectedHandler()}
              />
            ),
          }),
        ]
      : []),
    accessor("id", {
      header: "Visit ID",
      cell: (info) => info.getValue(),
      meta: {
        gqlSortKey: "id",
      },
    }),
    accessor("startTime", {
      header: "Visit Date",
      cell: (info) => {
        const value = info.getValue();
        return value ? dateFormatter.toDate(value) : "-";
      },
      meta: {
        gqlSortKey: "startTime",
      },
    }),
    accessor((row) => ({ startTime: row.startTime, endTime: row.endTime }), {
      id: "visitTime",
      header: "Visit Time",
      cell: (info) => {
        const { startTime, endTime } = info.getValue();
        // Use dateFormatter which handles GraphQL date types properly
        if (!startTime || !endTime) return "-";
        try {
          const startTimeStr = dateFormatter.toTime(startTime);
          const endTimeStr = dateFormatter.toTime(endTime);
          return `${startTimeStr} - ${endTimeStr}`;
        } catch (error) {
          return "-";
        }
      },
      meta: {
        gqlSortKey: "endTime",
      },
    }),
    accessor((row) => formatFullName(row.patient), {
      id: "patientFullName",
      header: "Patient",
      cell: (info) => {
        const name = info.getValue();
        const patientId = info.row.original.patientId;
        return (
          <Link
            color="blue.500"
            onClick={(e) => {
              e.stopPropagation();
              entityLink.open({
                event: e,
                entity: { type: "Patient", id: patientId },
              });
            }}
          >
            {name}
          </Link>
        );
      },
    }),
    accessor((row) => formatFullName(row.caregiver), {
      id: "caregiverFullName",
      header: "Caregiver",
      cell: (info) => {
        const name = info.getValue();
        const caregiverId = info.row.original.caregiverId;
        return (
          <Link
            color="blue.500"
            onClick={(e) => {
              e.stopPropagation();
              entityLink.open({
                event: e,
                entity: { type: "Caregiver", id: caregiverId },
              });
            }}
          >
            {name}
          </Link>
        );
      },
    }),
    accessor("clockinTime", {
      header: "Clock In",
      cell: (info) => {
        const value = info.getValue();
        return value ? dateFormatter.toTime(value) : "-";
      },
      meta: {
        gqlSortKey: "clockinTime",
      },
    }),
    accessor("clockoutTime", {
      header: "Clock Out",
      cell: (info) => {
        const value = info.getValue();
        return value ? dateFormatter.toTime(value) : "-";
      },
      meta: {
        gqlSortKey: "clockoutTime",
      },
    }),
    accessor("clockinType", {
      header: "Clock In Type",
      cell: (info) => info.getValue() || "-",
    }),
    accessor(
      (row) => ({
        location: row.clockinLocation,
        type: row.clockinType,
        patientId: row.patientId,
        patientAddressLocation: row.patient?.primaryAddress?.geo,
      }),
      {
        id: "clockinLocation",
        header: "Clock In Location",
        cell: (info) => {
          const { location, type, patientId, patientAddressLocation } = info.getValue();
          return (
            <ClockLocationCell
              clockType={type}
              location={location}
              patientAddressLocation={patientAddressLocation}
              patientId={patientId}
              visitType="clockin"
            />
          );
        },
      }
    ),
    accessor("clockoutType", {
      header: "Clock Out Type",
      cell: (info) => info.getValue() || "-",
    }),
    accessor(
      (row) => ({
        location: row.clockoutLocation,
        type: row.clockoutType,
        patientId: row.patientId,
        patientAddressLocation: row.patient?.primaryAddress?.geo,
      }),
      {
        id: "clockoutLocation",
        header: "Clock Out Location",
        cell: (info) => {
          const { location, type, patientId, patientAddressLocation } = info.getValue();
          return (
            <ClockLocationCell
              clockType={type}
              location={location}
              patientAddressLocation={patientAddressLocation}
              patientId={patientId}
              visitType="clockout"
            />
          );
        },
      }
    ),
    accessor((row) => row.billingInfo?.filingLimitDaysLeft, {
      id: "filingLimitDaysLeft",
      header: "Timely Filing",
      cell: (info) => {
        const days = info.getValue();
        if (days === null || days === undefined) return "-";
        const color = days < 0 ? "red" : days < 7 ? "orange" : "green";
        return <Text color={color}>{days} days</Text>;
      },
    }),
    accessor((row) => row.contract?.contractType.name ?? "", {
      id: "contractName",
      header: "Contract",
      cell: (info) => info.getValue() || "-",
    }),
    accessor((row) => row.serviceCode?.code ?? "", {
      id: "serviceCode",
      header: "Service Code",
      cell: (info) => info.getValue() || "-",
    }),
    accessor(
      (row) => (row.officeId != null ? officeNameById.get(String(row.officeId)) ?? "" : ""),
      {
        id: "officeName",
        header: "Office",
        cell: (info) => info.getValue() || "-",
      }
    ),
    accessor(
      (row) => ({
        clockinTime: row.clockinTime,
        clockoutTime: row.clockoutTime,
        evvAggregator: row.contract?.contractType.evvAggregator ?? null,
        emednySubmission: row.emednySubmission,
        tellusSubmission: row.tellusSubmission,
        sandataSubmission: row.sandataSubmission,
        hhaexchangeSubmission: row.hhaexchangeSubmission,
      }),
      {
        id: "evvStatus",
        header: "EVV Status",
        cell: (info) => {
          const {
            clockinTime,
            clockoutTime,
            evvAggregator,
            emednySubmission,
            tellusSubmission,
            sandataSubmission,
            hhaexchangeSubmission,
          } = info.getValue();
          // Convert LocalDateTime objects to ISO strings for the EVV status function
          const clockinTimeStr = clockinTime ? String(clockinTime) : null;
          const clockoutTimeStr = clockoutTime ? String(clockoutTime) : null;
          return (
            <EvvStatusCell
              clockinTime={clockinTimeStr}
              clockoutTime={clockoutTimeStr}
              emednySubmission={emednySubmission ?? null}
              evvAggregator={evvAggregator}
              hhaexchangeSubmission={hhaexchangeSubmission ?? null}
              sandataSubmission={sandataSubmission ?? null}
              tellusSubmission={tellusSubmission ?? null}
            />
          );
        },
        meta: {
          csvFn: (value) => mapEvvStatusToCsv(value),
        },
      }
    ),
    accessor(
      (row) => {
        const submittedAt =
          row.emednySubmission?.processedAt ??
          row.tellusSubmission?.createdAt ??
          row.sandataSubmission?.submittedAt ??
          row.hhaexchangeSubmission?.sentAt ??
          null;
        return submittedAt;
      },
      {
        id: "lastSubmittedAt",
        header: "Last Submitted At",
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          try {
            return dateFormatter.toDateTime(value);
          } catch {
            return "-";
          }
        },
      }
    ),
    accessor(
      (row) => {
        const errors = row.sandataSubmission?.errors;
        if (!errors || errors.length === 0) return null;
        return errors.filter(Boolean).join(", ");
      },
      {
        id: "evvError",
        header: "Error",
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "-";
          return (
            <Tooltip label={value} maxW="400px">
              <Text color="red.600" fontSize="sm" noOfLines={2}>
                {value}
              </Text>
            </Tooltip>
          );
        },
      }
    ),
    // A display column, not an accessor: accessor values are cached per row until the page
    // data changes, so a badge that arrives (or changes) later would never be shown.
    ...(isDevHelpEnabled
      ? [
          display({
            id: "devHelp",
            header: "Dev Help",
            cell: ({ row }) => (
              <EvvDevHelpBadge entry={devHelpStatusByVisitId.get(Number(row.original.id))} />
            ),
          }),
        ]
      : []),
    accessor(
      (row) => ({
        visitInstanceId: row.id,
        evvAggregator: row.contract?.contractType.evvAggregator ?? null,
        hasSandataSubmission: row.sandataSubmission !== null && row.sandataSubmission !== undefined,
      }),
      {
        id: "sandataPayload",
        header: "Payload",
        cell: (info) => {
          const { visitInstanceId, evvAggregator, hasSandataSubmission } = info.getValue();
          return (
            <SandataPayloadCell
              evvAggregator={evvAggregator}
              hasSandataSubmission={hasSandataSubmission}
              visitInstanceId={visitInstanceId}
            />
          );
        },
        meta: {
          // The payloads are fetched per opened visit, so there is nothing to
          // put in an export of the table.
          csvFn: () => "",
        },
      }
    ),
    ...(isFCAgency
      ? [
          accessor((row) => row.duties ?? [], {
            id: "duties",
            header: "Duties",
            cell: (info) => {
              const duties = info.getValue();
              if (!duties || duties.length === 0) return "-";
              return (
                <Popover placement="bottom-start" trigger="hover">
                  <PopoverTrigger>
                    <Badge colorScheme="blue" cursor="pointer" variant="subtle">
                      {duties.length} {duties.length === 1 ? "duty" : "duties"}
                    </Badge>
                  </PopoverTrigger>
                  <PopoverContent maxH="300px" maxW="350px" overflowY="auto" w="auto">
                    <PopoverBody>
                      <VStack align="start" spacing={1}>
                        {duties.map((duty, i) => (
                          <Text key={i} fontSize="sm">
                            {duty}
                          </Text>
                        ))}
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              );
            },
          }),
        ]
      : []),
  ];

  // Use the GraphQL data table hook
  const { query, globalFilters, setFilter, dataTableProps } = useGraphQLDataTable({
    document: GET_CLOCKED_VISIT_INSTANCES_QUERY as any,
    connection: "clockedVisitInstances",
    columns: columns,
    columnVisiblity: {},
    initialSorting: [{ id: "startTime", desc: true }],
    globalFilters: {
      initialState: {
        from: today,
        to: today,
        search: "",
      },
    },
    rowSelection: {
      state: rowSelection,
      onRowSelectionChange: handleRowSelectionChange,
      // Where dev help is available any visit with an EVV aggregator can be selected for it;
      // re-submit then only acts on the Sandata/HHAX ones among the selection.
      enableRowSelection: (row) =>
        isDevHelpEnabled
          ? row.original.contract?.contractType.evvAggregator != null
          : getResubmittableAggregator(row.original) !== null,
      getRowId: (row) => String(row.id),
    },
  });

  // This page tells the omnibar what its own filters are, while it is on screen.
  useClockedVisitsNewAiCapabilities({
    setFilter: (key, value) =>
      setFilter(key as Parameters<typeof setFilter>[0], value as Parameters<typeof setFilter>[1]),
  });

  nodesRef.current = (query.data?.clockedVisitInstances.nodes ?? []) as ClockedVisitInstance[];

  React.useEffect(() => {
    setVisibleVisitIds(nodesRef.current.map((node) => Number(node.id)));
  }, [query.data]);

  // Reset selection when filters change (selection intentionally persists across pages)
  React.useEffect(() => {
    setRowSelection({});
    setSelectedVisits(new Map());
  }, [globalFilters]);

  const resubmitMutation = useResubmitEvvVisits({
    onSuccess: () => {
      setRowSelection({});
      setSelectedVisits(new Map());
      confirmationDisclosure.onClose();
      queryClient.invalidateQueries({ queryKey: [GET_CLOCKED_VISIT_INSTANCES_QUERY] });
    },
    onError: confirmationDisclosure.onClose,
  });

  const selectedCount = selectedVisits.size;
  const resubmittableCount = [...selectedVisits.values()].filter(
    (visit) => getResubmittableAggregator(visit) !== null
  ).length;

  const handleResubmit = () => {
    const sandataIds: VisitInstanceId[] = [];
    const hhaxIds: VisitInstanceId[] = [];

    for (const visit of selectedVisits.values()) {
      const aggregator = getResubmittableAggregator(visit);
      if (aggregator === "sandata") {
        sandataIds.push(visit.id as VisitInstanceId);
      } else if (aggregator === "hhax") {
        hhaxIds.push(visit.id as VisitInstanceId);
      }
    }

    resubmitMutation.mutate([
      { aggregator: "sandata", visitInstanceIds: sandataIds },
      { aggregator: "hhax", visitInstanceIds: hhaxIds },
    ]);
  };

  // Two ways in: tick rows for a handful of visits, or upload a spreadsheet of
  // visit ids when finance is working from an export. The upload button is
  // always available so it is discoverable without selecting anything first.
  //
  // Returned as a fragment, not a Stack: DataTable drops actionNode straight into
  // the toolbar's `<Flex gap={2}>` alongside the meta buttons, and that Flex
  // stretches its children. Chakra's buttons carry a definite height so they stay
  // on the toolbar's top row, but a wrapping Stack has no height of its own -- it
  // stretches to the full (tall, wrapped) filter row and centres its buttons
  // halfway down it. Direct children line up, and the parent's gap spaces them.
  const actionNode = hasResubmitPermission ? (
    <>
      {resubmittableCount > 0 && (
        <Tooltip
          isDisabled={resubmittableCount <= MAX_RESUBMIT_VISITS}
          label={`Maximum ${MAX_RESUBMIT_VISITS} visits can be re-submitted at once`}
        >
          <Button
            colorScheme="blue"
            isDisabled={resubmittableCount > MAX_RESUBMIT_VISITS}
            size="md"
            onClick={confirmationDisclosure.onOpen}
          >
            Re-submit ({resubmittableCount})
          </Button>
        </Tooltip>
      )}
      {isDevHelpEnabled && selectedCount > 0 && (
        <Tooltip
          isDisabled={selectedCount <= EVV_DEV_HELP_MAX_VISITS_PER_REQUEST}
          label={`Maximum ${EVV_DEV_HELP_MAX_VISITS_PER_REQUEST} visits per request`}
        >
          <Button
            flexShrink={0}
            isDisabled={selectedCount > EVV_DEV_HELP_MAX_VISITS_PER_REQUEST}
            size="md"
            variant="outline"
            onClick={devHelpDisclosure.onOpen}
          >
            Need dev help ({selectedCount})
          </Button>
        </Tooltip>
      )}
      <Button flexShrink={0} size="md" variant="outline" onClick={uploadDisclosure.onOpen}>
        Re-submit from file
      </Button>
    </>
  ) : null;

  // Row click handler to open visit instance modal
  const handleClickRow = (
    event: React.MouseEvent<HTMLTableRowElement>,
    row: Row<ClockedVisitInstance>
  ) => {
    entityLink.open({ event, entity: { type: "VisitInstance", id: row.original.id } });
  };

  // Cast globalFilters to the correct type for type safety
  const filters = globalFilters as Partial<GetClockedVisitInstancesQueryVariables>;

  // Handler for "from" date that auto-adjusts "to" date if needed
  const handleFromDateChange = (newFrom: string | null) => {
    const to = filters.to as string | null;

    if (newFrom && to && newFrom > to) {
      // If new from date is after current to date, set to date to match from date
      setFilter("from", newFrom);
      setFilter("to", newFrom);
    } else {
      setFilter("from", newFrom);
    }
  };

  // Handler for "to" date that auto-adjusts "from" date if needed
  const handleToDateChange = (newTo: string | null) => {
    const from = filters.from as string | null;

    if (newTo && from && newTo < from) {
      // If new to date is before current from date, set from date to match to date
      setFilter("to", newTo);
      setFilter("from", newTo);
    } else {
      setFilter("to", newTo);
    }
  };

  const filterNode = (
    <>
      {/* Search */}
      <InputGroup width="md">
        <DebouncedInput
          debounce={200}
          placeholder="Search by patient or caregiver name..."
          value={filters.search ?? ""}
          onChange={(x) => setFilter("search", x)}
        />
        <InputRightElement>
          <SearchIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
        </InputRightElement>
      </InputGroup>

      {/* Date From */}
      <Input
        placeholder="From Date"
        type="date"
        value={(filters.from as any) ?? undefined}
        width="200px"
        onChange={(e) => handleFromDateChange(e.target.value || null)}
      />

      {/* Date To */}
      <Input
        placeholder="To Date"
        type="date"
        value={(filters.to as any) ?? ""}
        width="200px"
        onChange={(e) => handleToDateChange(e.target.value || null)}
      />

      <ContractTypeSelect
        multiple={true}
        value={toArrayOrNull(filters.contracts as any)}
        onChange={(x) => setFilter("contracts", x ?? null)}
      />

      <Select
        label="Service Codes"
        multiple={true}
        options={serviceCodeOptions}
        value={toArrayOrNull(filters.serviceCodes as any)}
        onChange={(x) => setFilter("serviceCodes", x ?? null)}
      />

      <OfficesSelect
        multiple={true}
        value={toArrayOrNull(filters.offices as any)}
        onChange={(x) => setFilter("offices", x ?? null)}
      />

      {/* TODO: Add AgencyBranchSelect component */}

      <AgencyMemberSelect
        filter={(x) =>
          ["Coordinator", "SeniorCoordinator", "HeadOfCoordination"].includes(x.jobTitle)
        }
        label="Coordinators"
        multiple={true}
        value={toArrayOrNull(filters.coordinators as any)}
        onChange={(x) => setFilter("coordinators", x ?? null)}
      />

      <AgencyTeamSelect
        multiple={true}
        value={toArrayOrNull(filters.teams as any)}
        onChange={(x) => setFilter("teams", x ?? null)}
      />

      <Select
        label="Certifications"
        multiple={true}
        options={certificationOptions}
        value={toArrayOrNull(filters.certifications as any)}
        onChange={(x) => setFilter("certifications", x ?? null)}
      />

      <Select
        label="Status"
        multiple={true}
        options={statusOptions}
        value={toArrayOrNull(filters.statuses as any)}
        onChange={(x) => setFilter("statuses", x ?? null)}
      />

      {isDevHelpEnabled && (
        <Select
          label="Dev help"
          multiple={true}
          options={devHelpStatusOptions}
          value={toArrayOrNull(filters.devHelpStatuses as any)}
          onChange={(x) => setFilter("devHelpStatuses", x ?? null)}
        />
      )}

      {/* Error text search */}
      <InputGroup width="250px">
        <DebouncedInput
          debounce={300}
          placeholder="Search errors..."
          value={(filters as any).evvErrorSearch ?? ""}
          onChange={(x) => setFilter("evvErrorSearch" as any, x || null)}
        />
        <InputRightElement>
          <SearchIcon color="gray.400" />
        </InputRightElement>
      </InputGroup>
    </>
  );

  return (
    <Page isLoading={query.isPending || query.isFetching}>
      <Page.Header>
        <Page.Title>
          Clock In & Out (New) ({query.data?.clockedVisitInstances.totalCount ?? "..."})
        </Page.Title>

        {hasDutyCodeMappingPermission && (
          <Tooltip label="EVV duty code mapping">
            <IconButton
              aria-label="EVV duty code mapping"
              icon={<SettingsIcon />}
              ml={2}
              size="sm"
              variant="ghost"
              onClick={() => stateService.go("app.evv.dutyCodeMapping")}
            />
          </Tooltip>
        )}
      </Page.Header>

      <Page.Content p={0}>
        <DataTable
          {...dataTableProps}
          actionNode={actionNode}
          filterNode={filterNode}
          onClickRow={handleClickRow}
        />
      </Page.Content>

      <ResubmitConfirmationDialog
        isLoading={resubmitMutation.isPending}
        isOpen={confirmationDisclosure.isOpen}
        visitCount={resubmittableCount}
        onClose={confirmationDisclosure.onClose}
        onConfirm={handleResubmit}
      />

      <EvvDevHelpModal
        isOpen={devHelpDisclosure.isOpen}
        visitInstanceIds={[...selectedVisits.values()].map((visit) => Number(visit.id))}
        onClose={devHelpDisclosure.onClose}
        onFlagged={() => {
          setRowSelection({});
          setSelectedVisits(new Map());
          queryClient.invalidateQueries({ queryKey: [EVV_DEV_HELP_STATUSES_KEY] });
          queryClient.invalidateQueries({ queryKey: [GET_CLOCKED_VISIT_INSTANCES_QUERY] });
        }}
      />

      <SandataResubmitUploadModal
        isOpen={uploadDisclosure.isOpen}
        onClose={uploadDisclosure.onClose}
        onSubmitted={() =>
          queryClient.invalidateQueries({ queryKey: [GET_CLOCKED_VISIT_INSTANCES_QUERY] })
        }
      />
    </Page>
  );
}

function mapEvvStatusToCsv(value: {
  clockinTime: unknown;
  clockoutTime: unknown;
  evvAggregator: string | null;
  emednySubmission: unknown;
  tellusSubmission: unknown;
  sandataSubmission: unknown;
  hhaexchangeSubmission: unknown;
}): string {
  const evvInfo = getEvvStatus(
    value.clockinTime ? String(value.clockinTime) : null,
    value.clockoutTime ? String(value.clockoutTime) : null,
    value.evvAggregator,
    (value.emednySubmission as any) ?? null,
    (value.tellusSubmission as any) ?? null,
    (value.sandataSubmission as any) ?? null,
    (value.hhaexchangeSubmission as any) ?? null
  );

  const sentOn =
    (value.tellusSubmission as any)?.createdAt ??
    (value.emednySubmission as any)?.processedAt ??
    (value.sandataSubmission as any)?.submittedAt ??
    (value.hhaexchangeSubmission as any)?.sentAt ??
    null;

  if (sentOn === null) {
    return evvInfo.tooltip.replace(/\n+/g, " | ");
  }

  const sentOnFormatted = dateFormatter.toDateTime(sentOn as any);
  return `${evvInfo.tooltip.replace(/\n+/g, " | ")} | Sent On: ${sentOnFormatted}`;
}
